/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    GRADE_CODE_TO_GRADE_NAME_MAP,
    GRADE_CODE_TO_PLP_MAP
} from "@/ts/constants";
import { NonTermViewTypeEnum } from "@/ts/enums/NonTermViewTypeEnum";
import { ReportingFeedbackTypeEnum } from "@/ts/enums/ReportingFeedbackTypeEnum";
import { ReportingGradeSubjectFeedbackModalTypeEnum } from "@/ts/enums/ReportingGradeSubjectFeedbackModalTypeEnum";
import { StudentAssignmentStatusEnum } from "@/ts/enums/StudentAssignmentStatusEnum";
import { SubjectFeedbackForGradeModalTypeEnum } from "@/ts/enums/SubjectFeedbackForGradeModalTypeEnum";
import {
    ElectivePerformance,
    QuizResultInterface,
    SubjectPerformance
} from "@/ts/interfaces/Reporting/AcademicTranscriptPreviewInterface";
import {
    AssignmentByTermInterface,
    AssignmentByTermSubjectInterface
} from "@/ts/interfaces/Reporting/AssignmentDetailsByTermInterface";
import { LessonByTermInterface } from "@/ts/interfaces/Reporting/CourseDetailByTermInterface";
import { CourseLessonDataInterface } from "@/ts/interfaces/Reporting/CourseLessonDataInterface";
import {
    GradeSubjectFavSubInterface,
    GradeSubjectFeedbackOptionInterface
} from "@/ts/interfaces/Reporting/GradeSubjectFeedbackInterface";
import { LessonActivityInterface } from "@/ts/interfaces/Reporting/LessonActivityInterface";
import { QuizDetailsWithActivitiesInterface } from "@/ts/interfaces/Reporting/QuizDetailsInterface";
import {
    AdditionalSubjectTableInterface,
    AssignmentSummarayPercentage,
    ElectiveTableWithCourseInterface,
    GradeDetailInterface,
    LessonSummarayPercentage,
    QuizImageTextInterface,
    QuizesSummarayPercentage,
    RawSubjectInterface,
    SelectedSubject,
    SubjectInterface,
    TermInterface,
    TermWithSubject
} from "@/ts/interfaces/Reporting/ReportingInterface";
import { ReportingGradeSubjectFeedbackData } from "@/ts/interfaces/Reporting/ReportingPremiumGradeSubjectFeedbackInterface";
import {
    WorkSampleLessonActivityInterface,
    WorkSampleUploadInterface
} from "@/ts/interfaces/Reporting/StudentFeedbackInterface";
import { StudentSubjectsInterface } from "@/ts/interfaces/Reporting/StudentSubjectsInterface";
import { SubjectFeedbackItem } from "@/ts/interfaces/Reporting/SubjectFeedbackInterface";
import { FileUploadInterfaceProps } from "@euka-mfes/bark";
import { router } from "@inertiajs/react";
import { format } from "date-fns";

import { getGradeCodeFromPlp } from "./utils";

export const calculateQuizesSummaryPercentage = (
    quizesList: QuizDetailsWithActivitiesInterface[]
): QuizesSummarayPercentage => {
    let noOfCompleted = 0;
    let maxQuizes = 0;
    let totalScore = 0;
    let totalMaxScore = 0;

    quizesList.forEach(quiz => {
        maxQuizes += 1;
        const attemptCount = quiz.attemptCount ?? 0;
        const score = quiz.score ?? 0;
        const maxScore = quiz.maxScore ?? 0;

        if (attemptCount > 0 && maxScore > 0) {
            noOfCompleted += 1;
            totalScore += score;
            totalMaxScore += maxScore;
        }
    });

    const percentage = maxQuizes > 0 ? (noOfCompleted / maxQuizes) * 100 : 0;
    const displayPercentage = displayPercentageValue(percentage);
    const aggregatePercentage =
        totalMaxScore > 0 ? (totalScore / totalMaxScore) * 100 : 0;
    const displayAggregatePercentage =
        displayPercentageValue(aggregatePercentage);

    return {
        noOfCompleted,
        maxQuizes,
        percentage: displayPercentage,
        aggregatePercentage: displayAggregatePercentage,
        msg:
            maxQuizes > 0
                ? `Quiz completion: ${displayPercentage}%`
                : "Quiz completion couldn’t be loaded"
    };
};

export const calculateAssignmentSummaryPercentage = (
    assignmentList: AssignmentByTermInterface[]
): AssignmentSummarayPercentage & {
    isAllMarked: boolean;
    markedPercentage: number;
} => {
    let noOfMarked = 0;
    let totalAssignments = 0;
    let totalPercentage = 0;

    assignmentList.forEach(term => {
        term.subjects.forEach(subject => {
            if (subject.status === StudentAssignmentStatusEnum.EXEMPTED) return;

            totalAssignments += 1;

            if (subject.status === StudentAssignmentStatusEnum.MARKED) {
                noOfMarked += 1;
                totalPercentage += Number(subject.assignmentPercentage ?? 0);
            }
        });
    });

    const isAllMarked = totalAssignments > 0 && noOfMarked === totalAssignments;

    const rawAvg =
        isAllMarked && totalAssignments > 0
            ? totalPercentage / totalAssignments
            : 0;

    const avgPercentage = displayPercentageValue(rawAvg);

    const rawMarkedPct =
        totalAssignments > 0 ? (noOfMarked / totalAssignments) * 100 : 0;

    const markedPercentage = displayPercentageValue(rawMarkedPct);

    return {
        totalAssignments,
        noOfMarked,
        avgPercentage,
        markedPercentage,
        isAllMarked,
        msg: ""
    };
};

export const calculateLessonPercentage = (
    lessonList: LessonByTermInterface[],
    totalNoOfLessons: number
): LessonSummarayPercentage => {
    let totlaCompletedCount = 0;

    lessonList.forEach(lesson => {
        totlaCompletedCount = totlaCompletedCount + lesson.noOfLessonsCompleted;
    });

    const percentage =
        totalNoOfLessons > 0
            ? (totlaCompletedCount / totalNoOfLessons) * 100
            : 0;

    return {
        noOfCompleted: totlaCompletedCount,
        maxLessons: totalNoOfLessons,
        percentage: displayPercentageValue(percentage),
        msg:
            totalNoOfLessons > 0
                ? `Lesson completion: ${percentage.toFixed(2)}%`
                : "Lesson completion couldn’t be loaded"
    };
};

const resolveLessonDetail = (
    detailWeek: LessonByTermInterface["weeks"][number] | undefined,
    lessonId: number,
    lessonIndex: number
) => {
    if (!detailWeek) return undefined;

    const lessonIdAsNumber = Number(lessonId);
    const lessonById = detailWeek.lessons.find(dl => {
        const detailLessonId = Number(dl.lessonId);
        return (
            !Number.isNaN(lessonIdAsNumber) &&
            !Number.isNaN(detailLessonId) &&
            detailLessonId === lessonIdAsNumber
        );
    });

    return lessonById ?? detailWeek.lessons[lessonIndex];
};

export const mappingGradeDetailByTerm = (
    subjectInfo: StudentSubjectsInterface,
    quizzData: QuizDetailsWithActivitiesInterface[],
    courseDetailsData: LessonByTermInterface[],
    assignmentData: AssignmentByTermInterface[],
    hasQuizError: boolean,
    hasLessonError: boolean,
    hasAssignmentError: boolean,
    hasSubjectError: boolean
): GradeDetailInterface => {
    const mappedTerms: TermInterface[] = subjectInfo.terms.map(
        subjectByTerm => {
            let totalCompletedLessonsForTerm = 0;
            // Calculate total lessons for this term once, outside the subjects loop
            const totalLessonsForTerm = getLessonCountFromSubjects(
                subjectByTerm.subjects
            );

            const subjects: SubjectInterface[] = subjectByTerm?.notIncluded
                ? []
                : subjectByTerm?.subjects.map(subject => {
                      // Find quiz data - ignore grade code for PLP subjects
                      const matchingQuiz = quizzData.find(
                          quiz =>
                              quiz.subjectId === subject.id &&
                              quiz.term === subjectByTerm.term.toString() &&
                              quiz.gradeCode === subject.gradeCode
                      );

                      const quizPercentage = hasQuizError
                          ? -1
                          : calculateQuizesPercentage(matchingQuiz);

                      const quizAttempts = hasQuizError
                          ? 0
                          : getQuizAttempt(matchingQuiz);

                      // Extract additional quiz data
                      const quizActivityId = hasQuizError
                          ? null
                          : (matchingQuiz?.activityId ?? null);

                      const quizScore = hasQuizError
                          ? -1
                          : (matchingQuiz?.score ?? 0);

                      const quizMaxScore = hasQuizError
                          ? -1
                          : (matchingQuiz?.maxScore ?? 0);

                      // Find lesson data - ignore grade code for PLP subjects
                      const lessonData = courseDetailsData.find(
                          lesson =>
                              lesson.subjectId === subject.id &&
                              lesson.term === subjectByTerm.term &&
                              (subject.plp
                                  ? lesson.gradeCode ===
                                    getGradeCodeFromPlp(subject.plp)
                                  : lesson.gradeCode === subject.gradeCode)
                      );

                      // Find assignment data
                      let termAssignmentData = getMatchingAssignmentSubjects(
                          assignmentData,
                          subjectByTerm.term,
                          subject.id
                      );

                      // If this is a PLP subject and no assignments found, try finding without grade restrictions
                      if (subject.plp && termAssignmentData.length === 0) {
                          termAssignmentData = assignmentData
                              .filter(
                                  assignment =>
                                      assignment.term === subjectByTerm.term
                              )
                              .flatMap(assignment =>
                                  assignment.subjects.filter(
                                      subj => subj.subjectId === subject.id
                                  )
                              );
                      }
                      const assignmentSubjectPercentage = hasAssignmentError
                          ? -1
                          : (Math.round(
                                termAssignmentData[0]?.assignmentPercentage
                            ) ?? null);
                      const assignmentOriginalPercentage =
                          termAssignmentData[0]?.assignmentPercentage ?? 0;
                      const assignmentStatus = termAssignmentData[0]?.status;

                      // Accumulate completed lessons for this term (skip exempt subjects)
                      if (!subject.isExempt) {
                          totalCompletedLessonsForTerm +=
                              lessonData?.noOfLessonsCompleted ?? 0;
                      }

                      const mergedWeeks = subject.allWeeks.map(allWeek => {
                          // try to find the same week in lessonData
                          const detailWeek = lessonData?.weeks.find(
                              w => Number(w.week) === Number(allWeek.week_id)
                          );

                          return {
                              week: allWeek.week_id,
                              lessonCount: allWeek.lesson_count,
                              lessons: allWeek.lessons
                                  .map((l, lessonIndex) => {
                                      const detailLesson = resolveLessonDetail(
                                          detailWeek,
                                          l.lesson_id,
                                          lessonIndex
                                      );

                                      return {
                                          lessonId: l.lesson_id,
                                          title: l.title,
                                          activityId:
                                              detailLesson?.activityId ?? 0,
                                          lessonName: l.title,
                                          lessonOrder: l.lesson_no ?? null,
                                          startedAt:
                                              detailLesson?.startedAt ?? null,
                                          completedAt:
                                              detailLesson?.completedAt ?? null,
                                          updatedAt:
                                              detailLesson?.updatedAt ?? null,
                                          hasStarted: Boolean(detailLesson)
                                      };
                                  })
                                  .sort((a, b) => {
                                      if (a.lessonOrder == null) return 1;
                                      if (b.lessonOrder == null) return -1;
                                      return a.lessonOrder - b.lessonOrder;
                                  })
                          };
                      });

                      return {
                          id: subject.id,
                          name: subject.name,
                          courseId: lessonData?.courseId ?? null,
                          plp: subject.plp,
                          noOfLessonsCompleted: hasLessonError
                              ? -1
                              : (lessonData?.noOfLessonsCompleted ?? 0),
                          totalLessons: subject.lessonCount ?? 0,
                          quizScore: quizPercentage,
                          isQuizColumnHide: !matchingQuiz,
                          quizAttemptCount: quizAttempts,
                          quizActivityId,
                          quizRawScore: quizScore,
                          quizMaxScore,
                          assignmentPercentage: assignmentSubjectPercentage,
                          assignmentOriginalPercentage,
                          assignmentStatus,
                          assignmentUUID: termAssignmentData[0]?.assignmentUUID,
                          // use the merged weeks instead of raw lessonData.weeks
                          weeks: mergedWeeks,
                          personalLearningPlanId:
                              subject.personalLearningPlanId,
                          feedbackStatus: subject.feedbackStatus,
                          gradeFeedbackStatus: subject.gradeFeedbackStatus,
                          hasWorkSample: subject.hasWorkSample ?? false,
                          assignments: subject.assignments ?? [],
                          isExempt: subject.isExempt ?? false
                      };
                  });

            const percentage =
                totalLessonsForTerm > 0 && totalCompletedLessonsForTerm > 0
                    ? displayPercentageValue(
                          (totalCompletedLessonsForTerm / totalLessonsForTerm) *
                              100
                      )
                    : 0;

            return {
                id: subjectByTerm.term,
                name: `Term ${subjectByTerm.term}`,
                percentage,
                subjects,
                isNotIncluded: subjectByTerm.notIncluded
            };
        }
    );

    // get Additional subjec mappings
    const additionalSubMapping = getMatchingInfoForAdditionalSubjects(
        subjectInfo,
        quizzData,
        courseDetailsData,
        hasQuizError,
        hasLessonError
    );

    // get Elective mappings
    const electiveSubMapping: ElectiveTableWithCourseInterface[] =
        getMatchingInfoForElectiveSubjects(
            subjectInfo,
            courseDetailsData,
            hasLessonError
        );

    const gradeDetail: GradeDetailInterface = {
        isCertificateCompleted: false,
        terms: mappedTerms,
        electives: electiveSubMapping,
        additionalSubjects: additionalSubMapping,
        hasQuizError,
        hasLessonError,
        hasAssignmentError,
        hasSubjectError,
        hasUniversityPathway: subjectInfo.hasUniversityPathway
    };

    return gradeDetail;
};

const getMatchingInfoForAdditionalSubjects = (
    subjectInfo: StudentSubjectsInterface,
    quizzData: QuizDetailsWithActivitiesInterface[],
    courseDetailsData: LessonByTermInterface[],
    hasQuizError: boolean,
    hasLessonError: boolean
): AdditionalSubjectTableInterface[] => {
    const subjects: AdditionalSubjectTableInterface[] =
        subjectInfo.additionalSubjects?.map(subject => {
            // Find quiz data - ignore grade code for PLP subjects
            const matchingQuiz = quizzData.find(
                quiz =>
                    quiz.subjectId === subject.subjectId &&
                    quiz.gradeCode === subject.gradeCode
            );
            const quizPercentage = hasQuizError
                ? -1
                : calculateQuizesPercentage(matchingQuiz);

            const quizAttempts = hasQuizError
                ? 0
                : getQuizAttempt(matchingQuiz);

            // Extract additional quiz data
            const quizActivityId = hasQuizError
                ? null
                : (matchingQuiz?.activityId ?? null);

            const quizScore = hasQuizError ? -1 : (matchingQuiz?.score ?? 0);

            const quizMaxScore = hasQuizError
                ? -1
                : (matchingQuiz?.maxScore ?? 0);

            // Find lesson data - ignore grade code for PLP subjects
            const lessonData =
                Array.isArray(courseDetailsData) && courseDetailsData.length > 0
                    ? courseDetailsData.find(
                          lesson =>
                              lesson.term === 999 &&
                              lesson.subjectId === subject.subjectId &&
                              lesson.courseId === subject.courseId
                      )
                    : null;

            const mergedWeeks = subject.allWeeks.map(allWeek => {
                // try to find the same week in lessonData
                const detailWeek = lessonData?.weeks.find(
                    w => Number(w.week) === Number(allWeek.week_id)
                );

                return {
                    week: allWeek.week_id,
                    lessonCount: allWeek.lesson_count,
                    lessons: allWeek.lessons.map((l, lessonIndex) => {
                        const detailLesson = resolveLessonDetail(
                            detailWeek,
                            l.lesson_id,
                            lessonIndex
                        );

                        return {
                            lessonId: l.lesson_id,
                            title: l.title,
                            activityId: detailLesson?.activityId ?? 0,
                            lessonName: detailLesson?.lessonName ?? l.title,
                            startedAt: detailLesson?.startedAt ?? null,
                            completedAt: detailLesson?.completedAt ?? null,
                            updatedAt: detailLesson?.updatedAt ?? null,
                            hasStarted: Boolean(detailLesson)
                        };
                    })
                };
            });

            return {
                id: subject.subjectId,
                courseId: subject.courseId,
                name: subject.name,
                plp: subject.plp,
                noOfLessonsCompleted: hasLessonError
                    ? -1
                    : (lessonData?.noOfLessonsCompleted ?? 0),
                totalLessons: subject.lessonCount ?? 0,
                quizScore: quizPercentage,
                isQuizColumnHide: !matchingQuiz,
                quizAttemptCount: quizAttempts,
                quizActivityId,
                quizRawScore: quizScore,
                quizMaxScore,
                // use the merged weeks instead of raw lessonData.weeks
                weeks: mergedWeeks,
                feedbackStatus: subject.feedbackStatus,
                gradeFeedbackStatus: subject.gradeFeedbackStatus,
                hasWorkSample: subject.hasWorkSample ?? false
            };
        });

    return subjects;
};

const getMatchingInfoForElectiveSubjects = (
    subjectInfo: StudentSubjectsInterface,
    courseDetailsData: LessonByTermInterface[],
    hasLessonError: boolean
): ElectiveTableWithCourseInterface[] => {
    const result: ElectiveTableWithCourseInterface[] = [];
    subjectInfo.electives?.forEach(subject => {
        const electiveCourses = subject.courses.map(course => {
            const lessonData =
                Array.isArray(courseDetailsData) && courseDetailsData.length > 0
                    ? courseDetailsData.find(
                          lesson => lesson.courseId === course.id
                      )
                    : null;

            const mergedWeeks = course.allWeeks.map(allWeek => {
                const detailWeek = lessonData?.weeks.find(
                    w => Number(w.week) === Number(allWeek.week_id)
                );

                return {
                    week: allWeek.week_id,
                    lessonCount: allWeek.lesson_count,
                    lessons: allWeek.lessons.map((l, lessonIndex) => {
                        const detailLesson = resolveLessonDetail(
                            detailWeek,
                            l.lesson_id,
                            lessonIndex
                        );

                        return {
                            lessonId: l.lesson_id,
                            title: l.title,
                            activityId: detailLesson?.activityId ?? 0,
                            lessonName: detailLesson?.lessonName ?? l.title,
                            startedAt: detailLesson?.startedAt ?? null,
                            completedAt: detailLesson?.completedAt ?? null,
                            updatedAt: detailLesson?.updatedAt ?? null,
                            hasStarted: Boolean(detailLesson)
                        };
                    })
                };
            });

            return {
                id: course.id,
                name: course.name,
                totalLessons: course.lessonCount ?? 0,
                noOfLessonsCompleted: hasLessonError
                    ? -1
                    : (lessonData?.noOfLessonsCompleted ?? 0),
                weeks: mergedWeeks,
                feedbackStatus: course.feedbackStatus ?? false,
                gradeFeedbackStatus: course.gradeFeedbackStatus ?? false
            };
        });

        result.push({
            subjectName: subject.subjectName,
            subjectId: subject.subjectId,
            electiveCourses
        });
    });

    return result;
};

const getMatchingAssignmentSubjects = (
    data: AssignmentByTermInterface[],
    term: number,
    subjectId: number
): AssignmentByTermSubjectInterface[] => {
    const termEntries = data.filter(item => item.term === term);

    return termEntries
        .flatMap(item => item.subjects)
        .filter(subject => subject.subjectId === subjectId);
};

export const calculateTotalLessons = (
    subjectResponse: StudentSubjectsInterface
): number => {
    let totalLessons = 0;
    let totalElectives = 0;
    let totalAdditionalSubjects = 0;

    for (const term of subjectResponse.terms) {
        for (const subject of term.subjects) {
            totalLessons += subject.lessonCount;
        }
    }

    for (const elective of subjectResponse.electives) {
        for (const course of elective.courses) {
            totalElectives += course.lessonCount;
        }
    }

    for (const additional of subjectResponse.additionalSubjects) {
        totalAdditionalSubjects += additional.lessonCount;
    }

    return totalLessons + totalElectives + totalAdditionalSubjects;
};

export const calculateQuizesPercentage = (
    quiz?: QuizDetailsWithActivitiesInterface
): number => {
    if (!quiz || quiz.activityId === null) return -99;

    if (quiz.score === null && quiz.attemptCount === 0) {
        return -100;
    }

    const hasValidQuizScore = (quiz.score ?? 0) > 0 && (quiz.maxScore ?? 0) > 0;

    if (!hasValidQuizScore) return 0;

    const score = quiz.score ?? 0;
    const maxScore = quiz.maxScore ?? 0;
    const percentage = (score / maxScore) * 100;

    return displayPercentageValue(percentage);
};

export const calculateQuizesPercentageOnPreview = (
    quiz: QuizResultInterface | null
): number => {
    if (!quiz) return -99;

    if (quiz.marks === null && quiz.attempts === 0) {
        return -100;
    }

    const hasValidQuizScore =
        (quiz.marks ?? 0) > 0 && (quiz.questionsCount ?? 0) > 0;

    if (!hasValidQuizScore) return 0;

    return displayPercentageValue(quiz.marks);
};

export const calculateQuizesPercentageOnGradePreview = (
    quiz: QuizResultInterface | null
): number => {
    if (!quiz) return -99;

    if (quiz?.marks === null && quiz?.marks === undefined) {
        return -100;
    }

    const hasValidQuizScore = (quiz?.marks ?? 0) > 0;

    if (!hasValidQuizScore) return 0;

    return displayPercentageValue(quiz?.marks);
};

export const getQuizAttempt = (
    quiz?: QuizDetailsWithActivitiesInterface
): number => {
    if (!quiz || quiz.activityId === null) return 0;

    const attemptCount = quiz.attemptCount ?? 0;
    const maxScore = quiz.maxScore ?? 0;

    if (attemptCount > 0 && maxScore > 0) {
        return attemptCount;
    }

    return 0;
};

export function getQuizImageText(
    score: number,
    attempts: number
): QuizImageTextInterface {
    switch (true) {
        case score >= 90:
            return {
                imgSource: "gold",
                displayText: `${score}%`,
                medalName: "Gold"
            };

        case score >= 80:
            return {
                imgSource: "silver",
                displayText: `${score}%`,
                medalName: "Silver"
            };

        case score >= 60:
            return {
                imgSource: "bronze",
                displayText: `${score}%`,
                medalName: "Bronze"
            };

        case score >= 0 && attempts > 0:
            return {
                imgSource: "pin",
                displayText: `${score}%`,
                medalName: "Pin"
            };

        case attempts === 0 && (score === -100 || score === 0):
            return {
                imgSource: "notCompleted",
                displayText: "Not completed",
                medalName: "Not completed"
            };

        case score === -1:
            return {
                imgSource: "notAvailable",
                displayText: "Quiz score couldn’t be loaded",
                medalName: "Not available"
            };

        default:
            return {
                imgSource: "notAvailable",
                displayText: "Quiz not available",
                medalName: "Not available"
            };
    }
}

export function getQuizSummaryImageText(score: number): QuizImageTextInterface {
    switch (true) {
        case score >= 90:
            return {
                imgSource: "gold",
                displayText: `${score}%`,
                medalName: "Gold"
            };

        case score >= 80:
            return {
                imgSource: "silver",
                displayText: `${score}%`,
                medalName: "Silver"
            };

        case score >= 60:
            return {
                imgSource: "bronze",
                displayText: `${score}%`,
                medalName: "Bronze"
            };

        case score >= 0:
            return {
                imgSource: "pin",
                displayText: `${score}%`,
                medalName: "Pin"
            };

        default:
            return {
                imgSource: "pin",
                displayText: `${score}%`,
                medalName: "Pin"
            };
    }
}

export const getLessonCountSumByTerm = (
    data: CourseLessonDataInterface[],
    termId: number
): number => {
    return data
        .filter(item => Number(item.term) === termId)
        .reduce((sum, item) => sum + item.lessonCount, 0);
};

export const getLessonCountFromSubjects = (
    subjects: RawSubjectInterface[]
): number => {
    return subjects
        .filter(subject => !subject.isExempt)
        .reduce((sum, subject) => sum + (subject.lessonCount ?? 0), 0);
};

export const getSelectedSubjectBySubjectAndTerm = (
    data: TermInterface[],
    subjectID: number,
    termId: number
): SubjectInterface | undefined => {
    const termData = data.find(term => term.id === termId);

    return termData?.subjects.find(subject => subject.id === subjectID);
};

/**
 * Converts a score into a display-friendly percentage value.
 *
 * - If the score is less than 1, it keeps one decimal place.
 * - If the score is 1 or greater, it returns the whole number by flooring the value.
 *
 * @param score - The numeric score to format.
 * @returns A number representing the formatted percentage value.
 */
export const displayPercentageValue = (score: number): number => {
    if (score < 1) {
        return parseFloat(score.toFixed(1));
    }
    return Math.floor(score);
};

export const handleBackTermAndNonTerm = (
    nonTermTypeParam: NonTermViewTypeEnum,
    plpGradeCode: number | undefined,
    eukaId: string,
    gradeCode: number,
    subjectId: number,
    termId: number
) => {
    if (
        nonTermTypeParam === null ||
        nonTermTypeParam === NonTermViewTypeEnum.DEFAULT
    ) {
        if (plpGradeCode) {
            const queryParams = { plp: plpGradeCode };

            router.get(
                route("parents.grade-term-detail", {
                    student: eukaId,
                    grade_code: gradeCode,
                    subject_id: subjectId,
                    term_id: termId
                }),
                queryParams
            );
        } else {
            router.get(
                route("parents.grade-term-detail", {
                    student: eukaId,
                    grade_code: gradeCode,
                    subject_id: subjectId,
                    term_id: termId
                })
            );
        }
    } else if (nonTermTypeParam === NonTermViewTypeEnum.ADDITIONAL_SUBJECTS) {
        const baseParams = {
            student: eukaId,
            grade_code: gradeCode,
            course_id: subjectId
        };

        router.get(route("parents.additional-subject-detail", baseParams));
    } else if (nonTermTypeParam === NonTermViewTypeEnum.ELECTIVES) {
        const baseParams = {
            student: eukaId,
            grade_code: gradeCode,
            course_id: subjectId
        };

        router.get(route("parents.elective-course-detail", baseParams));
    }
};

export const getTextFeildToolTipForNotAvailableQuiz = (
    gradeCode: number,
    plp: string | null,
    imgSource: string
): string | undefined => {
    const plpGradeCode = plp !== null ? getGradeCodeFromPlp(plp) : null;

    const grade =
        plpGradeCode === 999
            ? "PLP"
            : plpGradeCode
              ? GRADE_CODE_TO_GRADE_NAME_MAP[plpGradeCode]
              : GRADE_CODE_TO_GRADE_NAME_MAP[gradeCode];

    const textFieldToolTipContent =
        imgSource === "notAvailable"
            ? `Quiz results not available for this subject, either because no quiz is available or it isn’t part of the new ${grade} end of term quizzes.`
            : undefined;

    return textFieldToolTipContent;
};

export const getPlpLabel = (plpGradeCode: number | undefined) => {
    if (!plpGradeCode) return " ";
    if (plpGradeCode === 999) return " (PLP)";
    return ` (PLP - ${GRADE_CODE_TO_GRADE_NAME_MAP[plpGradeCode]}) `;
};

export const noOfLessonsCompletedInGradeSummary = (
    subjects: SubjectInterface[]
): number => {
    const values = subjects
        .filter(s => !s.isExempt)
        .map(s => s.noOfLessonsCompleted ?? 0);
    return values.reduce((sum, val) => sum + val, 0);
};

export const totalLessonsInGradeSummary = (
    subjects: SubjectInterface[]
): number => {
    const values = subjects
        .filter(s => !s.isExempt)
        .map(s => s.totalLessons ?? 0);
    return values.reduce((sum, val) => sum + val, 0);
};

export const quizScoreInGradeSummary = (
    subjects: SubjectInterface[]
): number => {
    const nonExempt = subjects ? subjects.filter(s => !s.isExempt) : [];

    if (!nonExempt || nonExempt.length === 0) {
        return -2;
    }

    const allCompleted = nonExempt.every(s => {
        const score = s.quizScore ?? null;
        const attempts = s.quizAttemptCount ?? 0;
        const isScore =
            score !== null && (score >= 1 || (attempts >= 1 && score === 0));
        return isScore;
    });

    const allNotCompleted = nonExempt.every(s => {
        return s.quizScore === -100;
    });

    const validScores = nonExempt
        .map(s => {
            const score = s.quizScore ?? null;
            const attempts = s.quizAttemptCount ?? 0;
            const isScore =
                score !== null &&
                (score >= 1 || (attempts >= 1 && score === 0));
            return isScore ? score : null;
        })
        .filter(v => v !== null) as number[];

    if (allNotCompleted || (validScores.length > 0 && !allCompleted)) {
        return -99;
    }

    if (validScores.length === 0) {
        return -2;
    }

    const average =
        validScores.reduce((sum, val) => sum + val, 0) / validScores.length;

    return displayPercentageValue(average ?? 0);
};

export const quizScoreInAdditionalSubjectGradeSummary = (
    subject: AdditionalSubjectTableInterface
): number => {
    if (!subject) {
        return -2;
    }

    const score = subject.quizScore ?? null;
    const attempts = subject.quizAttemptCount ?? 0;

    const isScore =
        score !== null && (score >= 1 || (attempts >= 1 && score === 0));

    if (!isScore) {
        return -2;
    }

    return displayPercentageValue(score ?? 0);
};

export const isQuizColumnHideInGradeSummary = (
    subjects: SubjectInterface[]
): boolean => {
    const values = subjects
        .filter(s => !s.isExempt)
        .map(s => s.isQuizColumnHide);
    return values.every(v => v === true);
};

export const quizAttemptCountInGradeSummary = (
    subjects: SubjectInterface[]
): number => {
    const values = subjects
        .filter(s => !s.isExempt)
        .map(s => s.quizAttemptCount ?? 0);
    return values.length ? values.reduce((sum, val) => sum + val, 0) : 0;
};

export const assignmentPercentageInGradeSummary = (
    subjects: SubjectInterface[]
): number => {
    if (!subjects || subjects.length === 0) {
        return -2;
    }

    const filteredSubjects = subjects.filter(
        subject =>
            subject.assignmentPercentage !== null &&
            subject.assignmentStatus !== StudentAssignmentStatusEnum.EXEMPTED &&
            subject.assignmentStatus !== undefined &&
            subject.assignmentStatus !== null
    );

    if (!filteredSubjects || filteredSubjects.length === 0) {
        return -2;
    }

    const allMarked = filteredSubjects.every(
        s =>
            s.assignmentStatus === StudentAssignmentStatusEnum.MARKED ||
            s.assignmentStatus ===
                StudentAssignmentStatusEnum.RESUBMISSION_REQUIRED
    );

    if (!allMarked) {
        return -99;
    }

    const values = filteredSubjects.map(
        s => s.assignmentOriginalPercentage ?? null
    );
    const nonNullValues = values.filter(v => v !== null) as number[];

    if (nonNullValues.length === 0) {
        return -2;
    }

    const average =
        nonNullValues.reduce((sum, val) => sum + val, 0) / nonNullValues.length;

    return displayPercentageValue(average ?? 0);
};

export const assignmentStatusInGradeSummary = (
    subjects: SubjectInterface[]
): StudentAssignmentStatusEnum => {
    const values = subjects.map(s => s.assignmentStatus ?? null);
    return values[0];
};

export const plpInGradeSummary = (
    subjects: SubjectInterface[]
): string | null => {
    const values = subjects.map(s => s.plp ?? null);
    return values[0] ?? null;
};

export const personalLearningPlanIdInGradeSummary = (
    subjects: SubjectInterface[]
): number | null => {
    const values = subjects.map(s => s.personalLearningPlanId ?? null);
    return values[0] ?? null;
};

export const feedbackStatusInGradeSummary = (
    subjects: SubjectInterface[]
): boolean => {
    const values = subjects.map(s => s.feedbackStatus ?? false);
    return values.some(v => v === true);
};

export const geradeFeedbackStatusInGradeSummary = (
    subjects: SubjectInterface[]
): boolean => {
    const values = subjects.map(s => s.gradeFeedbackStatus ?? false);
    return values.some(v => v === true);
};

export const getSubjectFeedbackById = (
    subjects: SubjectFeedbackItem[] | undefined,
    subjectId: number | undefined,
    selectedPLPId: number | null
): SubjectFeedbackItem | undefined => {
    return subjects?.find(
        subject =>
            subject.subjectId === subjectId &&
            subject.personalLearningPlanId === selectedPLPId
    );
};

export const getSubjectFeedbackForGradeById = (
    subjects: SubjectPerformance[] | undefined,
    subjectId: number | undefined,
    selectedPLPId: number | null,
    isAdditionalSubject: boolean,
    courseId: number | null
): SubjectPerformance | undefined => {
    if (subjects === undefined) {
        return undefined;
    }

    if (!isAdditionalSubject) {
        return subjects?.find(
            subject =>
                subject.id === subjectId &&
                subject.personalLearningPlanId === selectedPLPId
        );
    }
    return subjects?.find(
        subject =>
            subject.id === subjectId &&
            subject.personalLearningPlanId === selectedPLPId &&
            subject.isAdditionalSubject === true &&
            subject.courseId === courseId
    );
};

export const getElectiveFeedbackForGradeByIdAndCourseId = (
    electives: ElectivePerformance[] | undefined,
    subjectId: number | null,
    courseId: number | null,
    selectedPLPId: number | null
): ElectivePerformance | undefined => {
    return electives?.find(
        elective =>
            elective.id === subjectId &&
            elective.courseId === courseId &&
            elective.personalLearningPlanId === selectedPLPId
    );
};

export const totalElectiveCoursesInGradeSummary = (
    elective: ElectiveTableWithCourseInterface
): number => {
    return elective.electiveCourses.reduce(
        (sum, course) => sum + (course.totalLessons ?? 0),
        0
    );
};

export const totalElectiveCompletedCoursesInGradeSummary = (
    elective: ElectiveTableWithCourseInterface
): number => {
    return elective.electiveCourses.reduce(
        (sum, course) => sum + (course.noOfLessonsCompleted ?? 0),
        0
    );
};

export const totalElectivePresentageInGradeSummary = (
    elective: ElectiveTableWithCourseInterface
): number => {
    const total = totalElectiveCoursesInGradeSummary(elective);
    const completed = totalElectiveCompletedCoursesInGradeSummary(elective);
    return total > 0 ? (completed / total) * 100 : 0;
};

export const overallElectiveFeedbackStatusInGradeSummary = (
    elective: ElectiveTableWithCourseInterface
): boolean => {
    if (!elective?.electiveCourses || elective.electiveCourses.length === 0) {
        return false;
    }

    return elective.electiveCourses.every(
        course => course.feedbackStatus === true
    );
};

export const reportingGradeSubjectFeedbackData: ReportingGradeSubjectFeedbackData =
    {
        attitude_toward_school: ["attitude_toward_school"],
        work_ethic: ["work_ethic"],
        work_habits: ["work_habits"],
        summary: ["summary"],
        experience: [
            "home_school_experience_this_year",
            "home_school_experience_why"
        ],
        how_did_progress_thought_tasks: ["how_did_progress_thought_tasks"],
        has_been_working_on: ["has_been_working_on"],
        what_was_the_highlight_of_year: ["what_was_the_highlight_of_year"],
        what_was_the_lowlight_of_year: ["what_was_the_lowlight_of_year"],
        favourite_subject: [
            "what_was_the_favourite_subject",
            "favourite_subject_why"
        ],
        what_was_the_most_comfortable_learning_style: [
            "what_was_the_most_comfortable_learning_style"
        ],
        comment: ["comment"]
    };

export const gradeSubjectNextFeedbackType = (
    current: ReportingGradeSubjectFeedbackModalTypeEnum,
    isReportingEntitlementAvailable: boolean
): ReportingGradeSubjectFeedbackModalTypeEnum | null => {
    switch (current) {
        case ReportingGradeSubjectFeedbackModalTypeEnum.ATTITUDE_TOWARD_SCHOOL:
            return ReportingGradeSubjectFeedbackModalTypeEnum.WORK_ETHIC;
        case ReportingGradeSubjectFeedbackModalTypeEnum.WORK_ETHIC:
            return ReportingGradeSubjectFeedbackModalTypeEnum.WORK_HABIT;
        case ReportingGradeSubjectFeedbackModalTypeEnum.WORK_HABIT:
            return ReportingGradeSubjectFeedbackModalTypeEnum.SUMMARY;
        case ReportingGradeSubjectFeedbackModalTypeEnum.SUMMARY:
            return isReportingEntitlementAvailable
                ? ReportingGradeSubjectFeedbackModalTypeEnum.EXPERIENCE
                : ReportingGradeSubjectFeedbackModalTypeEnum.COMMENT;
        case ReportingGradeSubjectFeedbackModalTypeEnum.EXPERIENCE:
            return ReportingGradeSubjectFeedbackModalTypeEnum.TASKS;
        case ReportingGradeSubjectFeedbackModalTypeEnum.TASKS:
            return ReportingGradeSubjectFeedbackModalTypeEnum.WORKING_ON;
        case ReportingGradeSubjectFeedbackModalTypeEnum.WORKING_ON:
            return ReportingGradeSubjectFeedbackModalTypeEnum.HIGHLIGHT;
        case ReportingGradeSubjectFeedbackModalTypeEnum.HIGHLIGHT:
            return ReportingGradeSubjectFeedbackModalTypeEnum.LOWLIGHT;
        case ReportingGradeSubjectFeedbackModalTypeEnum.LOWLIGHT:
            return ReportingGradeSubjectFeedbackModalTypeEnum.FAVOURITE_SUBJECT;
        case ReportingGradeSubjectFeedbackModalTypeEnum.FAVOURITE_SUBJECT:
            return ReportingGradeSubjectFeedbackModalTypeEnum.LEARNING_STYLE;
        case ReportingGradeSubjectFeedbackModalTypeEnum.LEARNING_STYLE:
            return ReportingGradeSubjectFeedbackModalTypeEnum.COMMENT;
        case ReportingGradeSubjectFeedbackModalTypeEnum.COMMENT:
        default:
            return null;
    }
};

export const getGradeSubjectPreviousFeedbackType = (
    currentType: ReportingGradeSubjectFeedbackModalTypeEnum,
    isReportingEntitlementAvailable: boolean
): ReportingGradeSubjectFeedbackModalTypeEnum | null => {
    switch (currentType) {
        case ReportingGradeSubjectFeedbackModalTypeEnum.WORK_ETHIC:
            return ReportingGradeSubjectFeedbackModalTypeEnum.ATTITUDE_TOWARD_SCHOOL;
        case ReportingGradeSubjectFeedbackModalTypeEnum.WORK_HABIT:
            return ReportingGradeSubjectFeedbackModalTypeEnum.WORK_ETHIC;
        case ReportingGradeSubjectFeedbackModalTypeEnum.SUMMARY:
            return ReportingGradeSubjectFeedbackModalTypeEnum.WORK_HABIT;
        case ReportingGradeSubjectFeedbackModalTypeEnum.EXPERIENCE:
            return ReportingGradeSubjectFeedbackModalTypeEnum.SUMMARY;
        case ReportingGradeSubjectFeedbackModalTypeEnum.TASKS:
            return ReportingGradeSubjectFeedbackModalTypeEnum.EXPERIENCE;
        case ReportingGradeSubjectFeedbackModalTypeEnum.WORKING_ON:
            return ReportingGradeSubjectFeedbackModalTypeEnum.TASKS;
        case ReportingGradeSubjectFeedbackModalTypeEnum.HIGHLIGHT:
            return ReportingGradeSubjectFeedbackModalTypeEnum.WORKING_ON;
        case ReportingGradeSubjectFeedbackModalTypeEnum.LOWLIGHT:
            return ReportingGradeSubjectFeedbackModalTypeEnum.HIGHLIGHT;
        case ReportingGradeSubjectFeedbackModalTypeEnum.FAVOURITE_SUBJECT:
            return ReportingGradeSubjectFeedbackModalTypeEnum.LOWLIGHT;
        case ReportingGradeSubjectFeedbackModalTypeEnum.LEARNING_STYLE:
            return ReportingGradeSubjectFeedbackModalTypeEnum.FAVOURITE_SUBJECT;
        case ReportingGradeSubjectFeedbackModalTypeEnum.COMMENT:
            if (isReportingEntitlementAvailable) {
                return ReportingGradeSubjectFeedbackModalTypeEnum.LEARNING_STYLE;
            }
            return ReportingGradeSubjectFeedbackModalTypeEnum.SUMMARY;
        default:
            return null;
    }
};

export const gradeSubjectSubTitleByType = (
    type: ReportingGradeSubjectFeedbackModalTypeEnum,
    isReportingEntitlementAvailable: boolean,
    studentName: string,
    gradeLongName: string
): string => {
    const steps = isReportingEntitlementAvailable
        ? [
              `1 of 12: Which best describes ${studentName}’s attitude toward school?`,
              `2 of 12: Which best describes ${studentName}’s work ethic?`,
              `3 of 12: Which best describes ${studentName}’s work habits?`,
              `4 of 12: Which best summarises ${studentName}’s ${gradeLongName}?`,
              "5 of 12: How would you describe the home-school experience this year?",
              `6 of 12: How did ${studentName} progress through tasks?`,
              `7 of 12: ${studentName} has been working on`,
              `8 of 12: What was the highlight of ${studentName}’s year?`,
              `9 of 12: What was the lowlight of ${studentName}’s year?`,
              `10 of 12: What was ${studentName}’s favourite subject?`,
              `11 of 12: What was ${studentName}’s most comfortable learning style?`,
              "12 of 12: Overall comment"
          ]
        : [
              `1 of 5: Which best describes ${studentName}’s attitude toward school?`,
              `2 of 5: Which best describes ${studentName}’s work ethic?`,
              `3 of 5: Which best describes ${studentName}’s work habits?`,
              `4 of 5: Which best summarises ${studentName}’s ${gradeLongName}?`,
              "5 of 5: Overall comment"
          ];

    switch (type) {
        case ReportingGradeSubjectFeedbackModalTypeEnum.ATTITUDE_TOWARD_SCHOOL:
            return steps[0];
        case ReportingGradeSubjectFeedbackModalTypeEnum.WORK_ETHIC:
            return steps[1];
        case ReportingGradeSubjectFeedbackModalTypeEnum.WORK_HABIT:
            return steps[2];
        case ReportingGradeSubjectFeedbackModalTypeEnum.SUMMARY:
            return steps[3];
        case ReportingGradeSubjectFeedbackModalTypeEnum.EXPERIENCE:
            return steps[4];
        case ReportingGradeSubjectFeedbackModalTypeEnum.TASKS:
            return steps[5];
        case ReportingGradeSubjectFeedbackModalTypeEnum.WORKING_ON:
            return steps[6];
        case ReportingGradeSubjectFeedbackModalTypeEnum.HIGHLIGHT:
            return steps[7];
        case ReportingGradeSubjectFeedbackModalTypeEnum.LOWLIGHT:
            return steps[8];
        case ReportingGradeSubjectFeedbackModalTypeEnum.FAVOURITE_SUBJECT:
            return steps[9];
        case ReportingGradeSubjectFeedbackModalTypeEnum.LEARNING_STYLE:
            return steps[10];
        case ReportingGradeSubjectFeedbackModalTypeEnum.COMMENT:
            return isReportingEntitlementAvailable ? steps[11] : steps[4];
        default:
            return "";
    }
};

export const parseMetadata = (
    metadata?: Record<string, string> | string | null
): Record<string, any> => {
    if (!metadata) {
        return {};
    }

    if (typeof metadata === "string") {
        try {
            return JSON.parse(metadata);
        } catch (error) {
            console.error("Failed to parse metadata JSON:", error);
            return {};
        }
    }

    return metadata;
};

export const getAverageSubjects = (
    terms: TermInterface[]
): SubjectInterface[] => {
    const subjectMap: Record<string, SubjectInterface[]> = {};

    terms.forEach(term => {
        term.subjects
            .filter(subject => !subject.isExempt)
            .forEach(subject => {
            const plpKey = subject.plp ? subject.plp : "0";
            const key = `${subject.id}_${plpKey}`;

            if (!subjectMap[key]) {
                subjectMap[key] = [];
            }
            subjectMap[key].push(subject);
        });
    });

    const averagedSubjects: SubjectInterface[] = Object.values(subjectMap).map(
        (subjects: SubjectInterface[]) => {
            const [base] = subjects;
            const assignmentPercentageIndex =
                assignmentPercentageInGradeSummary(subjects);

            return {
                ...base,
                nonTermType: NonTermViewTypeEnum.DEFAULT,
                noOfLessonsCompleted:
                    noOfLessonsCompletedInGradeSummary(subjects),
                totalLessons: totalLessonsInGradeSummary(subjects),
                quizScore: quizScoreInGradeSummary(subjects),
                isQuizColumnHide: isQuizColumnHideInGradeSummary(subjects),
                quizAttemptCount: quizAttemptCountInGradeSummary(subjects),
                assignmentPercentage: assignmentPercentageIndex,
                assignmentStatus: assignmentStatusInGradeSummary(subjects),
                plp: plpInGradeSummary(subjects),
                weeks: [],
                allWeeks: [],
                personalLearningPlanId:
                    personalLearningPlanIdInGradeSummary(subjects),
                feedbackStatus: feedbackStatusInGradeSummary(subjects),
                gradeFeedbackStatus:
                    geradeFeedbackStatusInGradeSummary(subjects)
            };
        }
    );

    return averagedSubjects;
};

export const getAverageAdditionalSubjects = (
    additionalSubjects: AdditionalSubjectTableInterface[]
): SubjectInterface[] => {
    const averagedADSubjects: SubjectInterface[] = additionalSubjects.map(
        (subject: AdditionalSubjectTableInterface) => {
            return {
                id: subject.id,
                name: subject.name,
                courseId: subject.courseId,
                noOfLessonsCompleted: subject.noOfLessonsCompleted ?? 0,
                totalLessons: subject.totalLessons ?? 0,
                quizScore: quizScoreInAdditionalSubjectGradeSummary(subject),
                isQuizColumnHide: subject.isQuizColumnHide,
                quizAttemptCount: subject.quizAttemptCount ?? 0,
                assignmentPercentage: null,
                assignmentOriginalPercentage: 0,
                assignmentStatus: StudentAssignmentStatusEnum.TODO,
                plp: subject?.plp ?? null,
                weeks: [],
                allWeeks: [],
                personalLearningPlanId: null,
                feedbackStatus: subject.feedbackStatus ?? false,
                gradeFeedbackStatus: subject.feedbackStatus ?? false,
                nonTermType: NonTermViewTypeEnum.ADDITIONAL_SUBJECTS
            };
        }
    );

    return averagedADSubjects;
};

export const mapToLessonActivity = (
    workSample: WorkSampleLessonActivityInterface | null
): LessonActivityInterface | null => {
    if (!workSample) return null;

    return {
        lessonId: workSample.lessonId ?? 0,
        activityId: null,
        lesson: workSample.lesson ?? null,
        lessonName: "",
        lessonOrder: null,
        startedAt: "",
        completedAt: "",
        updatedAt: ""
    };
};

export const getSubjectIdByCourseIdByElectives = (
    data: ElectiveTableWithCourseInterface[] | undefined,
    courseId: number
): number | null => {
    if (!data) {
        return null;
    }
    for (const subject of data) {
        const foundCourse = subject.electiveCourses.find(
            course => course.id === courseId
        );
        if (foundCourse) {
            return subject.subjectId;
        }
    }
    return null;
};

export const getSubjectIdByCourseIdByAdditionalSubjects = (
    data: AdditionalSubjectTableInterface[] | undefined,
    courseId: number
): number | null => {
    if (!data) {
        return null;
    }
    const foundCourse = data.find(course => course.courseId === courseId);
    if (foundCourse) {
        return foundCourse.id;
    }

    return null;
};

export const getFilteredOptions = (
    options: GradeSubjectFeedbackOptionInterface[] | undefined,
    key: string,
    isReportingEntitlementAvailable: boolean,
    gradeDetail: GradeDetailInterface | null
): GradeSubjectFeedbackOptionInterface[] => {
    // Users who does not have reporting entitlement — simple exact match
    if (!isReportingEntitlementAvailable) {
        return options?.filter(option => option.key === key) || [];
    }

    const enumValue =
        key as (typeof ReportingGradeSubjectFeedbackModalTypeEnum)[keyof typeof ReportingGradeSubjectFeedbackModalTypeEnum];
    const matchedKeys = reportingGradeSubjectFeedbackData[enumValue];

    let keysToMatch: string[] = [];

    if (Array.isArray(matchedKeys)) {
        keysToMatch = matchedKeys;
    } else if (typeof matchedKeys === "object" && matchedKeys !== null) {
        keysToMatch = Object.keys(matchedKeys);
    } else if (typeof matchedKeys === "string") {
        keysToMatch = [matchedKeys];
    }

    if (keysToMatch.length > 0) {
        if (
            key ===
                ReportingGradeSubjectFeedbackModalTypeEnum.FAVOURITE_SUBJECT &&
            gradeDetail
        ) {
            const unSortedAllAvgSubjects: SubjectInterface[] =
                getAverageSubjects(gradeDetail?.terms ?? []);

            const allAvgSubjects = unSortedAllAvgSubjects.sort((a, b) =>
                a.name.localeCompare(b.name)
            );

            const radioOptions: GradeSubjectFeedbackOptionInterface[] =
                allAvgSubjects.map(subject => ({
                    id: subject.id,
                    optionUuid: "",
                    key: "what_was_the_favourite_subject",
                    type: "radio",
                    isPremium: 0,
                    optionText:
                        subject.plp !== null
                            ? `${subject.name} (${subject.plp})`
                            : subject.name,
                    icon: null,
                    order: 0,
                    isSubject: 0,
                    createdAt: "",
                    updatedAt: "",
                    plp: subject.plp !== null ? subject.plp : undefined,
                    nonTermType: NonTermViewTypeEnum.DEFAULT,
                    personalLearningPlanId: subject.personalLearningPlanId
                }));

            const allUnsortedAvgAdditionalSubjects: SubjectInterface[] =
                getAverageAdditionalSubjects(
                    gradeDetail?.additionalSubjects ?? []
                );

            const allAvgAdditionalSubjects =
                allUnsortedAvgAdditionalSubjects.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );

            if (allAvgAdditionalSubjects.length > 0) {
                for (const subject of allAvgAdditionalSubjects) {
                    radioOptions.push({
                        id: subject.id,
                        courseId: subject.courseId
                            ? subject.courseId
                            : undefined,
                        optionUuid: "",
                        key: "what_was_the_favourite_subject",
                        type: "radio",
                        isPremium: 0,
                        optionText:
                            subject.plp !== null
                                ? `${subject.name} (${subject.plp})`
                                : subject.name,
                        icon: null,
                        order: 0,
                        isSubject: 0,
                        createdAt: "",
                        updatedAt: "",
                        plp: subject.plp ?? undefined,
                        nonTermType: NonTermViewTypeEnum.ADDITIONAL_SUBJECTS,
                        personalLearningPlanId: subject.personalLearningPlanId
                    });
                }
            }
            const electiveOptions = getAverageElectives(gradeDetail);
            if (electiveOptions.length > 0) {
                radioOptions.push(...electiveOptions);
            }

            radioOptions.push({
                id: allAvgSubjects.length,
                optionUuid: "",
                key: "favourite_subject_why",
                type: "textarea",
                isPremium: 0,
                optionText: "favourite_subject_why",
                icon: null,
                order: 1,
                isSubject: 1,
                createdAt: "",
                updatedAt: ""
            });

            return radioOptions;
        }

        return (
            options?.filter(option => keysToMatch.includes(option.key)) || []
        );
    }

    return [];
};

export const getAverageElectives = (
    gradeDetail?: GradeDetailInterface
): GradeSubjectFeedbackOptionInterface[] => {
    const electives = gradeDetail?.electives ?? [];
    const radioOptions: GradeSubjectFeedbackOptionInterface[] = [];
    for (const electiveGroup of electives) {
        const { subjectId, subjectName } = electiveGroup;

        radioOptions.push({
            id: subjectId,
            courseId: undefined,
            optionUuid: "",
            key: "what_was_the_favourite_subject",
            type: "radio",
            isPremium: 0,
            optionText: subjectName,
            icon: null,
            order: 0,
            isSubject: 0,
            createdAt: "",
            updatedAt: "",
            plp: undefined,
            nonTermType: NonTermViewTypeEnum.ELECTIVES
        });
    }
    radioOptions.sort((a, b) => a.optionText.localeCompare(b.optionText));
    return radioOptions;
};

export const gradeFeedbackFavSubjectBuildOptionKey = (
    option: GradeSubjectFeedbackOptionInterface
): string => {
    const subjectId = option.courseId ?? option.id;

    const payload: GradeSubjectFavSubInterface = {
        subject_id: subjectId,
        personal_learning_plan_id: option.personalLearningPlanId ?? null,
        type: option.nonTermType ?? NonTermViewTypeEnum.DEFAULT
    };

    return JSON.stringify(payload);
};

export const convertWorkSampleToFileUpload = (
    item: WorkSampleUploadInterface
): FileUploadInterfaceProps => {
    return {
        uuid: item.uuid,
        assetId: String(item.assetId),
        fileUrl: item.fileDownloadUrl,
        fileName: item.fileName
    };
};

export const MAX_WAIT_TIME_FOR_WEB_SOCKET_RESPONSE_IN_MILI_SECONDS = 100000;

export const mapSubjectPerformanceToSubjectForGradeReport = (
    data: SubjectPerformance[]
): SubjectInterface[] => {
    return data.map(item => ({
        id: item.id,
        name: item.name,
        courseId: item.courseId,
        isAdditionalSubject: item.isAdditionalSubject,
        noOfLessonsCompleted: item.completedLessonCount ?? 0,
        totalLessons: item.totalLessonCount ?? 0,
        quizScore: 0,
        isQuizColumnHide: false,
        quizAttemptCount: 0,
        assignmentPercentage: item.assignmentPercentage,
        assignmentOriginalPercentage: item.assignmentPercentage ?? 0,
        assignmentStatus: StudentAssignmentStatusEnum.TODO,
        plp: item.isPLP ? GRADE_CODE_TO_PLP_MAP[item.gradeCode] : null,
        weeks: [],
        assignmentUUID: undefined,
        allWeeks: undefined,
        personalLearningPlanId: item.personalLearningPlanId,
        feedbackStatus: false,
        gradeFeedbackStatus: item.gradeFeedbackStatus,
        nonTermType: undefined,
        gradeCode: item.gradeCode
    }));
};

export const extractLessonNumber = (text: string): string => {
    const match = text.match(/Lesson\s+(\d+)\s*$/);
    return match ? `Lesson ${match[1]}` : text;
};

export const mapToSubjectInterface = (
    feedbackItem: SubjectFeedbackItem
): SubjectInterface => {
    return {
        id: feedbackItem.subjectId,
        name: feedbackItem.subjectName,
        plp: feedbackItem.plpGradeCode
            ? feedbackItem.plpGradeCode.toString()
            : null,
        courseId: null,
        noOfLessonsCompleted: 0,
        totalLessons: 0,
        quizScore: 0,
        isQuizColumnHide: false,
        quizAttemptCount: 0,
        assignmentPercentage: null,
        assignmentOriginalPercentage: 0,
        assignmentStatus: StudentAssignmentStatusEnum.TODO,
        weeks: [],
        assignmentUUID: undefined,
        allWeeks: [],
        personalLearningPlanId: feedbackItem.personalLearningPlanId,
        feedbackStatus: feedbackItem.feedbackStatus,
        gradeFeedbackStatus: false,
        nonTermType: NonTermViewTypeEnum.DEFAULT,
        gradeCode: feedbackItem.plpGradeCode ?? undefined
    };
};

export const mapSubjectFeedbackToTermWithSubjectForNonTerm = (
    item: SubjectFeedbackItem
): TermWithSubject => {
    const selectedSubject: SelectedSubject = {
        id: item.subjectId,
        name: item.subjectName,
        plp: item.plpText ?? null,
        noOfLessonsCompleted: 0,
        totalLessons: 0,
        quizScore: 0,
        quizAttemptCount: 0,
        isQuizColumnHide: false,
        assignmentPercentage: null,
        assignmentStatus: StudentAssignmentStatusEnum.TODO,
        courseId: item.courseId,
        weeks: [],
        personalLearningPlanId: item.personalLearningPlanId ?? null,
        feedbackStatus: item.feedbackStatus
    };

    return {
        id: 999,
        name: "",
        percentage: 0,
        isNotIncluded: false,
        selectedSubject,
        hasAssignmentError: false,
        hasLessonError: false,
        hasQuizError: false,
        hasSubjectError: false
    };
};

export const mapReportingFeedbackType = (
    type: ReportingFeedbackTypeEnum | null
): SubjectFeedbackForGradeModalTypeEnum | null => {
    if (type === null) return null;

    const mapping: Record<
        ReportingFeedbackTypeEnum,
        SubjectFeedbackForGradeModalTypeEnum | null
    > = {
        [ReportingFeedbackTypeEnum.FEEDBACK]:
            SubjectFeedbackForGradeModalTypeEnum.COMMENT,
        [ReportingFeedbackTypeEnum.PERFORMANCE]:
            SubjectFeedbackForGradeModalTypeEnum.PERFORMANCE,
        [ReportingFeedbackTypeEnum.ATTITUDE]:
            SubjectFeedbackForGradeModalTypeEnum.ATTITUDE_AND_WORK_ETHIC,
        [ReportingFeedbackTypeEnum.WORK_SAMPLE]: null
    };

    return mapping[type] ?? null;
};

export const mapSubjectPerformanceToFeedbackItem = (
    subject: SubjectPerformance
): SubjectFeedbackItem => {
    return {
        subjectId: subject.id,
        courseId: subject.courseId,
        subjectName: subject.name,
        isPlp: subject.isPLP,
        feedbackStatus: subject.gradeFeedbackStatus,
        plpGradeCode: subject.gradeCode ?? null,
        personalLearningPlanId: subject.personalLearningPlanId ?? null,
        plpText: subject.comment ?? undefined,
        answers: null // Map later if needed
    };
};

export const mapElectivePerformanceToFeedbackItem = (
    subject: ElectivePerformance
): SubjectFeedbackItem => {
    return {
        subjectId: subject.id,
        courseId: subject.courseId,
        subjectName: subject.name,
        isPlp: subject.isPLP,
        feedbackStatus: subject.gradeFeedbackStatus,
        plpGradeCode: subject.gradeCode ?? null,
        personalLearningPlanId: subject.personalLearningPlanId ?? null,
        plpText: subject.comment ?? undefined,
        answers: null // Map later if needed
    };
};

export const getErrorMessageForGradeFeedback = (
    type: ReportingGradeSubjectFeedbackModalTypeEnum
): string => {
    switch (type) {
        case ReportingGradeSubjectFeedbackModalTypeEnum.EXPERIENCE:
        case ReportingGradeSubjectFeedbackModalTypeEnum.FAVOURITE_SUBJECT:
            return "Select an option and provide a reason to continue.";

        case ReportingGradeSubjectFeedbackModalTypeEnum.HIGHLIGHT:
        case ReportingGradeSubjectFeedbackModalTypeEnum.LOWLIGHT:
            return "Please provide a reason to continue.";

        case ReportingGradeSubjectFeedbackModalTypeEnum.LEARNING_STYLE:
            return "Select up to two options to continue.";

        default:
            return "Select an option to continue.";
    }
};

export const mapGradeReportErrorDataToTermWithSubject = (
    wsSubject: WorkSampleLessonActivityInterface
): TermWithSubject => {
    let selectedSubject: SelectedSubject | null = null;

    if (wsSubject?.subject) {
        selectedSubject = {
            id: wsSubject?.subject.id,
            name: wsSubject?.subject.name,
            plp: null, // item.plpText ?? null,
            noOfLessonsCompleted: 0,
            totalLessons: 0,
            quizScore: 0,
            quizAttemptCount: 0,
            isQuizColumnHide: false,
            assignmentPercentage: null,
            assignmentStatus: StudentAssignmentStatusEnum.TODO,
            courseId: null, // item.courseId,
            weeks: wsSubject.weeks ? wsSubject.weeks : [],
            personalLearningPlanId: wsSubject.personalLearningPlanId
                ? wsSubject.personalLearningPlanId
                : null,
            feedbackStatus: false
        };
    }

    return {
        id: parseInt(wsSubject.term),
        name:
            wsSubject.term && wsSubject.term !== "999"
                ? `Term ${wsSubject.term}`
                : (wsSubject.course?.name ?? ""),
        percentage: 0,
        isNotIncluded: false,
        selectedSubject,
        hasAssignmentError: false,
        hasLessonError: false,
        hasQuizError: false,
        hasSubjectError: false
    };
};

export const isWorkSampleComingFromWH = (filename: string) => {
    return /^work-sample-\d+\.\w+$/i.test(filename);
};

export const formatAccessUntil = (endDate: string): string => {
    try {
        return format(new Date(endDate), "dd MMM yyyy");
    } catch {
        return endDate;
    }
};
